import { FC, useState } from 'react';
import { Input, Modal } from 'antd';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { PlatformForm as Form } from '@/shared/components/forms';

interface RenameResourceModalProps {
    currentName: string;
    open: boolean;
    resourceLabel: string;
    onCancel: () => void;
    onRename: (name: string) => Promise<void>;
}

const RenameResourceModal: FC<RenameResourceModalProps> = ({
    currentName,
    open,
    resourceLabel,
    onCancel,
    onRename,
}) => {
    const [name, setName] = useState(currentName);
    const [renaming, setRenaming] = useState(false);

    const cancel = () => {
        if (renaming) {
            return;
        }

        setName(currentName);
        onCancel();
    };

    const rename = async () => {
        const normalizedName = name.trim();

        if (!normalizedName || normalizedName === currentName || renaming) {
            return;
        }

        try {
            setRenaming(true);
            await onRename(normalizedName);
            setName('');
            onCancel();
        } catch {
            toast.error(`Unable to rename ${resourceLabel.toLowerCase()}`);
        } finally {
            setRenaming(false);
        }
    };

    return (
        <Modal
            afterOpenChange={(nextOpen) => {
                if (nextOpen) {
                    setName(currentName);
                    setRenaming(false);
                }
            }}
            open={open}
            title={`Rename ${resourceLabel}`}
            okText='Rename'
            okButtonProps={{ disabled: !name.trim() || name.trim() === currentName }}
            confirmLoading={renaming}
            onCancel={cancel}
            onOk={() => void rename()}
        >
            <Form layout='vertical' requiredMark={false}>
                <Form.Item label='Name' style={{ marginBottom: 0 }}>
                    <Input value={name} maxLength={250} onChange={(event) => setName(event.target.value)} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default RenameResourceModal;
